/**
 * Main layout for WMS DAOUXUN
 */
import React, { useState, useEffect, useCallback, useMemo, Suspense, lazy } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
// Removed: import { AnimatePresence } from 'framer-motion'; - causes TDZ error in production
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Box,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Badge,
  Tooltip,
  useTheme,
  useMediaQuery,
  Tabs,
  Tab,
  Paper,
  Collapse,
  Fab,
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Inventory as InventoryIcon,
  Inventory2 as Inventory2Icon,
  ShoppingCart as OrdersIcon,
  People as UsersIcon,
  Warehouse as WarehouseIcon,
  Analytics as ReportsIcon,
  Settings as SettingsIcon,
  Notifications as NotificationsIcon,
  AccountCircle as AccountIcon,
  Logout as LogoutIcon,
  Home as HomeIcon,
  LocalShipping as ShippingIcon,
  Assignment as TasksIcon,
  Business as BusinessIcon,
  People as PeopleIcon,
  SmartToy as AIIcon,
  NetworkCheck as MonitoringIcon,
  BarChart as ChartsIcon,
  Store as StoreIcon,
  Schedule as ScheduleIcon,
  Widgets as WidgetsIcon,
  Webhook as WebhookIcon,
  Build as ConfigurationIcon,
  Undo as UndoIcon,
  Search as SearchIcon,
  Route as RouteIcon,
  Label as LabelIcon,
  Speed as SpeedIcon,
  Palette as PaletteIcon,
  Psychology as PsychologyIcon,
  Monitor as MonitorIcon,
  Link as LinkIcon,
  Support as SupportIcon,
  AssignmentReturn as AssignmentReturnIcon,
  AssignmentInd as AssignmentIndIcon,
  Assessment as AssessmentIcon,
  Map as MapIcon,
  Security as SecurityIcon,
  Shield as ShieldIcon,
  VerifiedUser as VerifiedUserIcon,
  Group as GroupIcon,
  MonetizationOn as MonetizationOnIcon,
  TrackChanges as TrackChangesIcon,
  FlightTakeoff as FlightTakeoffIcon,
  Chat as ChatIcon,
  Code as CodeIcon,
  AccountTree as CanvasIcon,
  ViewModule as ViewModuleIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  Close as CloseIcon,
  Add as AddIcon,
  Timeline as TimelineIcon,
  BugReport as BugReportIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext.helpers.js';
import { useThemeMode } from '../contexts/ThemeContext.helpers.js';
import SidebarWMS from '../components/manager/SidebarWMS';
import SidebarClient from '../components/client/SidebarClient';
import ThemeToggle from '../components/common/ThemeToggle.jsx';
import OnboardingProvider from '../components/onboarding/OnboardingProvider';
import GlobalSearch from '../components/common/GlobalSearch.jsx';
import UserPreferences from '../components/common/UserPreferences.jsx';
import NotificationSystem from '../components/common/NotificationSystem.jsx';
import useAdvancedShortcuts from '../hooks/useAdvancedShortcuts.js';
import { useGlobalErrorHandler } from '../hooks/useErrorTracking';
import { useActivityLogger } from '../hooks/useActivityLogger';
import { useRealTimeNotifications } from '../hooks/useRealTimeNotifications';
import useGlobalShortcuts from '../hooks/useGlobalShortcuts';
import ShortcutHelp from '../components/common/ShortcutHelp.jsx';
import Breadcrumbs from '../components/common/Breadcrumbs.jsx';
import SidebarCustomizer from '../components/common/SidebarCustomizer.jsx';
import ThemeCustomizer from '../components/common/ThemeCustomizer.jsx';
import { SessionRecovery } from '../components/common/SessionRecovery';
import ParticleBackground from '../components/ui/ParticleBackground.jsx';
import AnimatedBackground from '../components/ui/AnimatedBackground.jsx';
import { getIconComponent } from '../utils/iconLibrary.js';
import { useTranslation } from 'react-i18next';
import { ROLE_LABELS } from '../utils/userRoles.js';
import ModuleTabs from '../components/navigation/ModuleTabs.jsx';

import { api } from '../services/api';
import { API_ENDPOINTS } from '../config/api.config.js';
import { setupIntelligentPrefetch } from '../utils/routePrefetch';

// Lazy load complex components to prevent circular dependency issues in production build
const FloatingChatbot = lazy(() => import('../components/common/FloatingChatbot'));
const FloatingActions = lazy(() => import('../components/common/FloatingActions'));
const PerformanceOptimizer = () => null;
const WorkspaceInterface = lazy(() => import('../components/ui/WorkspaceInterface'));

const DRAWER_WIDTH = 240;

/**
 * Navigation by role with tabbed sections
 * Each role has a streamlined set of navigation items organized by category
 * Admin/Manager use SidebarWMS for detailed navigation, this is for quick access
 */
const getNavigationItems = (user) => {
  if (!user) return [];

  // Role-based navigation - 8 main sections max per role
  // Quick access navigation - detailed navigation is in SidebarWMS/SidebarClient
  const navigationByRole = {
    admin: [
      { textKey: 'nav.dashboard', icon: DashboardIcon, path: '/admin/dashboard', category: 'main' },
      { textKey: 'nav.operations', icon: RouteIcon, path: '/admin/orders', category: 'operations' },
      { textKey: 'nav.inventory', icon: InventoryIcon, path: '/admin/products', category: 'inventory' },
      { textKey: 'nav.logistics', icon: ShippingIcon, path: '/admin/logistics', category: 'logistics' },
      { textKey: 'nav.business', icon: MonetizationOnIcon, path: '/admin/financial', category: 'business' },
      { textKey: 'nav.intelligence', icon: AIIcon, path: '/admin/ai', category: 'intelligence' },
      { textKey: 'nav.visualization', icon: MapIcon, path: '/admin/warehouse-3d', category: 'visualization' },
      { textKey: 'nav.settings', icon: SettingsIcon, path: '/admin/configuration', category: 'settings' }
    ],
    manager: [
      { textKey: 'nav.dashboard', icon: DashboardIcon, path: '/manager/dashboard', category: 'main' },
      { textKey: 'nav.operations', icon: RouteIcon, path: '/manager/orders', category: 'operations' },
      { textKey: 'nav.inventory', icon: InventoryIcon, path: '/manager/inventory', category: 'inventory' },
      { textKey: 'nav.logistics', icon: ShippingIcon, path: '/manager/logistics', category: 'logistics' },
      { textKey: 'nav.workforce', icon: UsersIcon, path: '/manager/workforce', category: 'workforce' },
      { textKey: 'nav.analytics', icon: ReportsIcon, path: '/manager/analytics', category: 'analytics' },
      { textKey: 'nav.warehouse3d', icon: MapIcon, path: '/manager/warehouse-3d', category: 'visualization' },
      { textKey: 'nav.returnsForecast', icon: ChartsIcon, path: '/manager/returns-forecast', category: 'analytics' },
      { textKey: 'nav.billing', icon: MonetizationOnIcon, path: '/manager/invoices', category: 'billing' }
    ],
    operator: [
      { textKey: 'nav.dashboard', icon: DashboardIcon, path: '/operator/dashboard', category: 'main' },
      { textKey: 'nav.tasks', icon: TasksIcon, path: '/operator/tasks', category: 'tasks' },
      { textKey: 'nav.picking', icon: TasksIcon, path: '/operator/picking', category: 'picking' },
      { textKey: 'nav.planning', icon: ScheduleIcon, path: '/operator/planning', category: 'planning' },
      { textKey: 'nav.inventory', icon: InventoryIcon, path: '/operator/inventory_items', category: 'inventory' },
      { textKey: 'nav.cycleCounting', icon: Inventory2Icon, path: '/operator/cycle-counting', category: 'inventory' },
      { textKey: 'nav.locations', icon: WarehouseIcon, path: '/operator/warehouse_locations', category: 'locations' },
      { textKey: 'nav.returns', icon: UndoIcon, path: '/operator/returns', category: 'returns' },
      { textKey: 'nav.tms', icon: ShippingIcon, path: '/operator/tms', category: 'tms' }
    ],
    client: [
      { textKey: 'nav.dashboard', icon: DashboardIcon, path: '/client/dashboard', category: 'main' },
      { textKey: 'nav.orders', icon: OrdersIcon, path: '/client/orders', category: 'orders' },
      { textKey: 'nav.products', icon: InventoryIcon, path: '/client/products', category: 'products' },
      { textKey: 'nav.inventory', icon: Inventory2Icon, path: '/client/inventory', category: 'inventory' },
      { textKey: 'nav.expeditions', icon: ShippingIcon, path: '/client/expeditions', category: 'shipping' },
      { textKey: 'nav.tms', icon: ShippingIcon, path: '/client/tms', category: 'tms' },
      { textKey: 'nav.oms', icon: BusinessIcon, path: '/client/oms', category: 'oms' },
      { textKey: 'nav.reports', icon: ReportsIcon, path: '/client/reports', category: 'reports' },
      { textKey: 'nav.billing', icon: MonetizationOnIcon, path: '/client/billing-estimate', category: 'billing' },
      { textKey: 'nav.support', icon: SupportIcon, path: '/client/support-modern', category: 'support' }
    ],
    selfservice: [
      { textKey: 'nav.dashboard', icon: DashboardIcon, path: '/fba/dashboard', category: 'main' },
      { textKey: 'nav.shipments', icon: ShippingIcon, path: '/fba/shipments', category: 'shipments' },
      { textKey: 'nav.inventory', icon: InventoryIcon, path: '/fba/inventory', category: 'inventory' },
      { textKey: 'nav.reports', icon: ReportsIcon, path: '/fba/reports', category: 'reports' },
      { textKey: 'nav.settings', icon: SettingsIcon, path: '/fba/settings', category: 'settings' }
    ],
    warehouse: [
      { textKey: 'nav.dashboard', icon: DashboardIcon, path: '/warehouse/dashboard', category: 'main' },
      { textKey: 'nav.locations', icon: WarehouseIcon, path: '/warehouse/locations', category: 'locations' },
      { textKey: 'nav.inventory', icon: InventoryIcon, path: '/warehouse/inventory', category: 'inventory' },
      { textKey: 'nav.reports', icon: ReportsIcon, path: '/warehouse/reports', category: 'reports' }
    ],
    team_leader: [
      { textKey: 'nav.dashboard', icon: DashboardIcon, path: '/team-leader/dashboard', category: 'main' },
      { textKey: 'nav.team', icon: UsersIcon, path: '/team-leader/team', category: 'team' },
      { textKey: 'nav.tasks', icon: TasksIcon, path: '/team-leader/tasks', category: 'tasks' },
      { textKey: 'nav.reports', icon: ReportsIcon, path: '/team-leader/reports', category: 'reports' }
    ]
  };

  const rolesArray = Array.isArray(user.permissions) && user.permissions.length > 0
    ? user.permissions
    : (user.role ? [user.role] : []);

  const normalizedRoles = rolesArray.map(r => {
    if (typeof r === 'string' && r.startsWith('client')) return 'client';
    return r;
  });

  const items = new Map();

  const resolvedRoles = normalizedRoles.length > 0 ? normalizedRoles : ['client'];
  resolvedRoles.forEach(role => {
    const roleItems = navigationByRole[role] || [];
    roleItems.forEach(item => {
      items.set(item.path, item);
    });
  });

  if (items.size === 0) {
    (navigationByRole['client'] || []).forEach(item => items.set(item.path, item));
  }

  const customItems = JSON.parse(localStorage.getItem('sidebar_custom_items') || '[]');
  customItems.forEach(customItem => {
    if (customItem.visible) {
      const IconComponent = getIconComponent(customItem.icon);
      items.set(customItem.path, {
        text: customItem.label,
        icon: IconComponent,
        path: customItem.path,
        isCustom: true
      });
    }
  });

  return Array.from(items.values());
};

const MainLayout = ({ children }) => {
  const { t } = useTranslation(['common', 'shared']);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useGlobalErrorHandler({ enabled: true });
  const { logActivity } = useActivityLogger('navigation');
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isAuthenticated } = useAuth();
  const { darkMode, toggleTheme } = useThemeMode();
  const { notifications: _rtNotifications, unreadCount: _rtUnreadCount } = useRealTimeNotifications(user?.id);
  const { shortcuts: _globalShortcutMap } = useGlobalShortcuts();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [preferencesOpen, setPreferencesOpen] = useState(false);
  const [sidebarCustomizerOpen, setSidebarCustomizerOpen] = useState(false);
  const [themeCustomizerOpen, setThemeCustomizerOpen] = useState(false);

  // State for Chat/Editor/Canvas workspace interface
  const [workspaceInterfaceOpen, setWorkspaceInterfaceOpen] = useState(false);
  const [_activeTab, setActiveTab] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [_chatMessages, setChatMessages] = useState([]);
  const [_editorCode, _setEditorCode] = useState('// Welcome to the WMS DAOUXUN editor\n// Ready to code!');
  const [_canvasWorkflow, setCanvasWorkflow] = useState({ nodes: [], edges: [] });

  const ENABLE_LABS = ((typeof window !== 'undefined' && window.REACT_APP_ENABLE_LABS === 'true') || (typeof localStorage !== 'undefined' && localStorage.getItem('ENABLE_LABS') === 'true'));

  useEffect(() => {
    if (!ENABLE_LABS) return;
    const fetchChatMessages = async () => {
      try {
        const response = await api.get(`${API_ENDPOINTS.AI_TOOLS}/chat/history`);
        if (response.status >= 200 && response.status < 300) {
          const data = response.data;
          setChatMessages(data.messages || []);
        }
      } catch (error) {
        setChatMessages([]);
      }
    };
    fetchChatMessages();
  }, [ENABLE_LABS]);

  useEffect(() => {
    if (!ENABLE_LABS) return;
    const fetchCanvasWorkflow = async () => {
      try {
        const response = await api.get(`${API_ENDPOINTS.CANVAS}/workflow`);
        if (response.status >= 200 && response.status < 300) {
          const data = response.data;
          setCanvasWorkflow(data.workflow || { nodes: [], edges: [] });
        }
      } catch (error) {
        setCanvasWorkflow({ nodes: [], edges: [] });
      }
    };
    fetchCanvasWorkflow();
  }, [ENABLE_LABS]);

  // Advanced keyboard shortcuts system
  const shortcutCallbacks = {
    onOpenSearch: () => setSearchOpen(true),
    onCloseModal: () => {
      setSearchOpen(false);
      setPreferencesOpen(false);
      setWorkspaceInterfaceOpen(false);
    },
    onToggleTheme: toggleTheme,
    onOpenNotifications: () => {
      // Logic to open notifications (already managed by NotificationSystem)
    },
    onOpenPreferences: () => setPreferencesOpen(true),
    onShowHelp: () => setHelpVisible(true),
    onRefresh: () => window.location.reload(),
    onNewItem: () => {
      const path = location.pathname;
      if (path.startsWith('/admin/returns')) {
        navigate('/admin/returns/new');
      } else if (path.startsWith('/admin/picking')) {
        navigate('/admin/picking-management');
      } else if (path.startsWith('/admin/label-management')) {
        navigate('/admin/label-management');
      } else if (path.startsWith('/admin/user')) {
        navigate('/admin/user-management');
      } else {
        navigate('/admin/configuration');
      }
    },
    onSave: () => {
    },
    onFind: () => setSearchOpen(true),
    // Actions for the workspace interface
    onOpenWorkspaceInterface: () => setWorkspaceInterfaceOpen(true),
    onToggleFullscreen: () => setIsFullscreen(!isFullscreen),
        onSwitchToChat: () => { setActiveTab(0); setWorkspaceInterfaceOpen(true); },
        onSwitchToEditor: () => { setActiveTab(1); setWorkspaceInterfaceOpen(true); },
        onSwitchToCanvas: () => { setActiveTab(2); setWorkspaceInterfaceOpen(true); }
  };

  const {
    isHelpVisible,
    setIsHelpVisible: setHelpVisible,
    getShortcutHelp,
    _currentContext
  } = useAdvancedShortcuts(shortcutCallbacks);

  // Mobile menu management
  const handleDrawerToggle = useCallback(() => {
    setMobileOpen(!mobileOpen);
  }, [mobileOpen]);

  // User menu management
  const handleUserMenuOpen = useCallback((event) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleUserMenuClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  // Navigation
  const _handleNavigation = useCallback((path) => {
    navigate(path);
    logActivity('navigate', { path });
    if (isMobile) {
      setMobileOpen(false);
    }
  }, [navigate, isMobile, logActivity]);

  // Logout
  const handleLogout = useCallback(async () => {
    handleUserMenuClose();
    await logout();
    navigate('/login');
  }, [handleUserMenuClose, logout, navigate]);

  // Navigation items must be computed before early return
  const navigationItems = useMemo(() => {
    if (!user) return [];
    const base = getNavigationItems(user);
    if (base && base.length > 0) return base;
    return [
      { textKey: 'nav.dashboard', icon: DashboardIcon, path: '/client/dashboard' },
      { textKey: 'nav.orders', icon: OrdersIcon, path: '/client/orders' },
      { textKey: 'nav.marketplace', icon: StoreIcon, path: '/client/marketplace' },
      { textKey: 'nav.wms', icon: WarehouseIcon, path: '/client/wms-dashboard' },
      { textKey: 'nav.tms', icon: ShippingIcon, path: '/client/tms-dashboard' },
      { textKey: 'nav.inventory', icon: InventoryIcon, path: '/client/inventory' },
      { textKey: 'nav.billing', icon: MonetizationOnIcon, path: '/client/billing' },
      { textKey: 'nav.reports', icon: ReportsIcon, path: '/client/reports' },
      { textKey: 'nav.support', icon: SupportIcon, path: '/client/support' }
    ];
  }, [user]);

  useEffect(() => {
    const routeImports = {
      '/admin/dashboard': () => import('../pages/admin/AdminDashboard'),
      '/admin/orders': () => import('../pages/admin/OrderCockpitPage'),
      '/admin/products': () => import('../pages/admin/InventoryCockpitPage'),
      '/admin/configuration': () => import('../pages/admin/ConfigurationPage'),
      '/manager/dashboard': () => import('../pages/manager/ManagerDashboard'),
      '/manager/returns-forecast': () => import('../pages/manager/ReturnsForecastDashboard'),
      '/operator/dashboard': () => import('../pages/operator/NewOperatorDashboard'),
      '/operator/picking': () => import('../pages/operator/PickingPage'),
      '/client/dashboard': () => import('../pages/client/ClientDashboard'),
      '/client/orders': () => import('../pages/client/OrdersPage'),
      '/client/billing-estimate': () => import('../pages/client/BillingPageModern'),
    };
    setupIntelligentPrefetch(location.pathname, routeImports);
  }, [location.pathname]);

  if (!isAuthenticated || !user) {
    return null;
  }
  // Drawer content
  // Determine correct Sidebar based on role
  const isClient = user?.role === 'client' || user?.roles?.includes('client');
    const SidebarComponent = isClient ? SidebarClient : SidebarWMS;
    const drawerContent = <SidebarComponent collapsed={isMobile && !mobileOpen} />;

  const currentPage = location.pathname;

  return (
    <OnboardingProvider
      userId={user?.id}
      userRole={user?.role}
      currentPage={currentPage}
      apiBaseUrl="/api/v1/onboarding"
      enabled={true}
    >
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* AppBar */}
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          ml: { md: `${DRAWER_WIDTH}px` },
          zIndex: theme.zIndex.drawer + 1,
          background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
          boxShadow: '0 2px 12px rgba(0, 0, 0, 0.15)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
        }}
      >
        <Toolbar sx={{ minHeight: '64px', px: 2 }}>
          {/* Mobile burger menu */}
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>

          {/* Integrated global search bar */}
          <Box sx={{
            flexGrow: 1,
            maxWidth: 600,
            mx: 2,
            position: 'relative'
          }}>
            <Box
              onClick={() => setSearchOpen(true)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                background: 'rgba(255, 255, 255, 0.15)',
                backdropFilter: 'blur(10px)',
                borderRadius: '12px',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                px: 2,
                py: 1,
                cursor: 'pointer',
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  background: 'rgba(255, 255, 255, 0.25)',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)'
                }
              }}
            >
              <SearchIcon sx={{
                color: 'rgba(255, 255, 255, 0.7)',
                mr: 1.5,
                fontSize: 20
              }} />
              <Typography
                variant="body2"
                sx={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  flexGrow: 1,
                  userSelect: 'none'
                }}
              >
                {t('common.searchWMS', 'Search across entire WMS...')}
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  ml: 2,
                  px: 1,
                  py: 0.5,
                  borderRadius: '6px',
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.15)'
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.6)',
                    fontSize: '11px',
                    fontWeight: 500
                  }}
                >
                  Ctrl+K
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Fast shortcuts - Displayed only for clients */}
          {user?.roles?.includes('client') && (
            <Box sx={{ display: 'flex', gap: 1, mr: 2 }}>
              <Tooltip title={t('client.parcelTracking', 'Parcel Tracking')} arrow>
                <IconButton
                  color="inherit"
                  onClick={() => navigate('/client/tracking')}
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: '12px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.15)',
                    position: 'relative',
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      background: 'rgba(255, 255, 255, 0.2)',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)'
                    }
                  }}
                >
                  <TrackChangesIcon sx={{ fontSize: 20 }} />
                  {/* Subtle notification badge */}
                  <Box sx={{
                    position: 'absolute',
                    top: 6,
                    right: 6,
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    background: '#ff4444',
                    border: '1px solid white',
                    animation: 'pulse 2s infinite'
                  }} />
                </IconButton>
              </Tooltip>

              <Tooltip title={t('common.returns', 'Returns')} arrow>
                <IconButton
                  color="inherit"
                  onClick={() => navigate('/client/retours')}
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: '12px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.15)',
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      background: 'rgba(255, 255, 255, 0.2)',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)'
                    }
                  }}
                >
                  <UndoIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>

              <Tooltip title="Shipments" arrow>
                <IconButton
                  color="inherit"
                  onClick={() => navigate('/client/expeditions')}
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: '12px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.15)',
                    position: 'relative',
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      background: 'rgba(255, 255, 255, 0.2)',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)'
                    }
                  }}
                >
                  <FlightTakeoffIcon sx={{ fontSize: 20 }} />
                  {/* Subtle notification badge */}
                  <Box sx={{
                    position: 'absolute',
                    top: 6,
                    right: 6,
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    background: '#4caf50',
                    border: '1px solid white'
                  }} />
                </IconButton>
              </Tooltip>

              <Tooltip title={t('common.billing', 'Billing')} arrow>
                <IconButton
                  color="inherit"
                  onClick={() => navigate('/client/billing')}
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: '12px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.15)',
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      background: 'rgba(255, 255, 255, 0.2)',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)'
                    }
                  }}
                >
                  <MonetizationOnIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
            </Box>
          )}

          {/* Improved theme toggle */}
          <Box sx={{ mr: 1 }}>
            <ThemeToggle
              darkMode={darkMode}
              onToggle={toggleTheme}
              variant="icon"
            />
          </Box>

          {/* Advanced Theme Customization */}
          <Box sx={{ mr: 1 }}>
            <Tooltip title={t('common.advancedThemes', 'Advanced Themes')}>
              <IconButton
                onClick={() => setThemeCustomizerOpen(true)}
                sx={{
                  background: 'linear-gradient(45deg, #7c3aed, #06b6d4)',
                  color: 'white',
                  width: 40,
                  height: 40,
                  '&:hover': {
                    background: 'linear-gradient(45deg, #8b5cf6, #0891b2)',
                    transform: 'scale(1.1)',
                    boxShadow: '0 0 20px rgba(124, 58, 237, 0.5)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                <PaletteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Improved notification system */}
          <Box sx={{ mr: 1 }}>
            <NotificationSystem />
          </Box>

          {/* Menu user improved */}
          <IconButton
            color="inherit"
            onClick={handleUserMenuOpen}
            sx={{
              ml: 1,
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)'
              }
            }}
          >
            <Avatar sx={{
              width: 36,
              height: 36,
              bgcolor: 'secondary.main',
              border: '2px solid rgba(255, 255, 255, 0.2)',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
              fontSize: '14px',
              fontWeight: 600
            }}>
              {user.firstName?.[0] || user.username?.[0] || 'U'}
            </Avatar>
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Menu user */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleUserMenuClose}
        onClick={handleUserMenuClose}
        PaperProps={{
          elevation: 3,
          sx: {
            mt: 1.5,
            minWidth: 200,
            '& .MuiMenuItem-root': {
              px: 2,
              py: 1
            }
          }
        }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
            {user.firstName} {user.lastName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {user.email}
          </Typography>
          <Typography variant="caption" color="primary">
            {user.roles?.map(role => ROLE_LABELS[role]).join(', ')}
          </Typography>
        </Box>
        <Divider />
        <MenuItem onClick={() => navigate('/client/profile')}>
          <ListItemIcon>
            <AccountIcon fontSize="small" />
          </ListItemIcon>
          {t('common.profileLabel', 'Profile')}
        </MenuItem>
        <MenuItem onClick={() => {
          setPreferencesOpen(true);
          setAnchorEl(null);
        }}>
          <ListItemIcon>
            <SettingsIcon fontSize="small" />
          </ListItemIcon>
          {t('common.preferences', 'Preferences')}
        </MenuItem>
        <MenuItem onClick={() => navigate('/settings')}>
          <ListItemIcon>
            <SettingsIcon fontSize="small" />
          </ListItemIcon>
          {t('common.parameters', 'Parameters')}
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          {t('common.logout', 'Logout')}
        </MenuItem>
      </Menu>

      {/* Drawer */}
      <Box
        component="nav"
        sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: DRAWER_WIDTH
            }
          }}
        >
          {drawerContent}
        </Drawer>

        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: DRAWER_WIDTH,
              borderRight: '1px solid',
              borderColor: 'divider'
            }
          }}
          open
        >
          {drawerContent}
        </Drawer>
      </Box>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          minHeight: '100vh',
          backgroundColor: 'grey.50'
        }}
      >
        <Toolbar /> {/* Spacer for AppBar */}

        {/* Module Tabs Navigation - WMS/OMS/TMS/TTMS/CRM */}
        <ModuleTabs />

        {/* Breadcrumbs Navigation */}
        <Box sx={{ px: 3, pt: 2 }}>
          <Breadcrumbs showRecentPages={true} />
        </Box>

        <Box sx={{ p: 3, pt: 2 }}>
          {children}
        </Box>
      </Box>

      {/* Search globale */}
      <GlobalSearch
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
      />

      {/* Preferences user */}
      <UserPreferences
        open={preferencesOpen}
        onClose={() => setPreferencesOpen(false)}
      />

      {/* Contextual help */}
      <ShortcutHelp
        open={isHelpVisible}
        onClose={() => setHelpVisible(false)}
        shortcutHelp={getShortcutHelp()}
      />

      {/* Sidebar customizer */}
      <SidebarCustomizer
        open={sidebarCustomizerOpen}
        onClose={() => setSidebarCustomizerOpen(false)}
        currentNavigation={navigationItems}
        onSave={(customItems) => {
          localStorage.setItem('sidebar_custom_items', JSON.stringify(customItems));
          // Navigation could be updated here
        }}
      />

      {/* Advanced theme customizer */}
      <ThemeCustomizer
        open={themeCustomizerOpen}
        onClose={() => setThemeCustomizerOpen(false)}
      />

      {/* Visual background effects - Disabled for standard theme */}
      {/* <AnimatedBackground theme="cyberpunk" intensity="medium" />
      <ParticleBackground 
        theme="cyberpunk" 
        particleCount={60} 
        interactive={true} 
        opacity={0.7} 
      /> */}

      {/* Assistant IA flottant - Lazy loaded */}
      <Suspense fallback={null}>
        {ENABLE_LABS && <FloatingChatbot />}
      </Suspense>

      {/* Actions flottantes globales - Lazy loaded */}
      <Suspense fallback={null}>
        <FloatingActions />
      </Suspense>

      {/* Chat/Editor/Canvas Workspace Interface */}
      <SpeedDial
        ariaLabel="WMS Workspace Interface"
        sx={{
          position: 'fixed',
          bottom: 80,
          right: 80,
          zIndex: 1300
        }}
        icon={<SpeedDialIcon icon={<ViewModuleIcon />} openIcon={<CloseIcon />} />}
        onClose={() => { }}
        onOpen={() => { }}
        open={false}
        direction="up"
      >
        <SpeedDialAction
          key="unified"
          icon={<ViewModuleIcon />}
          tooltipTitle={t('common.unifiedInterface', 'Unified Interface')}
          onClick={() => setWorkspaceInterfaceOpen(true)}
        />
      </SpeedDial>

      {/* Workspace Interface - Lazy loaded */}
      <Suspense fallback={null}>
                <WorkspaceInterface
                  open={workspaceInterfaceOpen}
                  onClose={() => setWorkspaceInterfaceOpen(false)}
        />
      </Suspense>

      <SessionRecovery
        onRestore={() => window.location.reload()}
        onClear={() => {}}
        autoRestore={false}
        showDialog={true}
      />
    </Box>
    </OnboardingProvider>
  );
};

export default React.memo(MainLayout);

